import React, { useContext, useState, useCallback, useMemo } from "react";
import { getDataFromStorage } from "../../../util";
import AlertContext from "../../../context/AlertContext";
import { useParams } from "react-router-dom";
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import Loading from "../../Notification-Loading/Loading/Loading";
import useCommonFunctionHooks from "../../../Hooks/useCommonFunctionHooks";
import UserActivityCard from "./UserActivityCard";
import UserActivityReportTable from "./UserActivityReportTable";
import DateRangeFilter from "./DateRangeFilter";
import { useInfiniteScroll } from "./useInfiniteScroll";

export default function UserActivityReport({learnerid}) {
  const Alert = useContext(AlertContext);
  const { id } = useParams();
  const axios = useAxiosPrivate();
  const { getHeaderDetailsForAxios, getCatchBlockDetails } = useCommonFunctionHooks();

  //states
  const [activities, setActivities] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [filter, setFilter] = useState({
    type: "all",
    startDate: null,
    endDate: null,
  });
  const [pageResetFlag, setPageResetFlag] = useState(false);


  // Memoize params to prevent unnecessary re-renders
  const learnerId = useMemo(() => Alert.isAdmin || getDataFromStorage("role") === "hr" ? learnerid : getDataFromStorage("learnerid"), [Alert.isAdmin, learnerid]);
  const batchId = useMemo(() => Alert.isAdmin || getDataFromStorage("role") === "hr" ? id : localStorage.getItem("batchid"), [Alert.isAdmin, id]);
  const learnerIdRef = React.useRef(learnerId);


  const fetchData = useCallback(async (page) => {

    if (learnerIdRef.current !== learnerId) {
      // If learnerId has changed, reset page to 0
      setPage(0);
      learnerIdRef.current = learnerId;
      setPageResetFlag(!pageResetFlag);
      return;
    }
    const isAllFilter = filter.type === "all";
    const params = { all: isAllFilter, page: page };
    if (!isAllFilter) {
      params.sd = filter.startDate ? filter.startDate : null;
      params.ed = filter.endDate ? filter.endDate : null;
    }
   
    const queryString = new URLSearchParams(params).toString();
    try {
      const response = await axios.get(`node/learner/${learnerId}/batch/${batchId}/activity?${queryString}`,
        getHeaderDetailsForAxios()
      );

      const data = response?.data?.data;
      const newActivities = data?.activities || [];

      setActivities(prev => (page === 0 ? newActivities : [...prev, ...newActivities]));
      if (data?.chartData && page === 0) setChartData(data?.chartData);
      return data?.nextPage;
    } catch (error) {
      getCatchBlockDetails(error);
      return [];
    }
  }, [learnerId, batchId, filter.type, filter.startDate, filter.endDate, pageResetFlag]);


const { lastElementRef, loading, hasMore, setPage } = useInfiniteScroll(fetchData);

const handleFilterChange = useCallback((newFilter) => {
  setFilter(prev => {
    // Only update if something actually changed
    if (prev.type === newFilter.type &&
        prev.startDate === newFilter.start &&
        prev.endDate === newFilter.end) {
      return prev;
    }
    setPage(0);
    // setHasMore(true);
    return {
      type: newFilter.type,
      startDate: newFilter.start || null,
      endDate: newFilter.end || null,
    };
  });
}, [setPage]);

  const filterComponent = useMemo(
    () => <DateRangeFilter onFilterChange={handleFilterChange} activeFilterType={filter.type} />,
    [handleFilterChange, filter.type]
  );

  return (
    <div className="userActivityReportContainer">
      {loading && ( <Loading overlay/> )}
      {(chartData?.batchData || chartData?.otherData) && (
        <div className="userActivityReport">
          <UserActivityCard
            title="Batch Activity"
            showFilter
            filterComponent={filterComponent}
            chartData={chartData?.batchData}
            grouping={chartData?.grouping}
          />

          <UserActivityCard
            title="Other Activity"
            chartData={chartData?.otherData}
            grouping={chartData?.grouping}
          />

          <UserActivityReportTable activityData={activities}/>

          <div ref={lastElementRef} className="observer-container">
            {loading && (
              <p className="loading-text">Loading more activities...</p>
            )}
            {!hasMore && activities?.length > 0 && (
              <p className="end-text">No more activities to show.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}